<script lang="ts">
  import { useTransitStore } from '../../store/transit'
  import { useRiderStore } from '../../store/rider'
  import { useVehicleWatchStore } from '../../store/vehicleWatch'
  import { storeSelect } from '../../lib/store.svelte'
  import { Colors, getRouteColor, routeBadgeLabel } from '../../ui/colors'
  import {
    bulletFontPx,
    bulletText,
    GLASS_BG,
    panelFrame,
    tileButtonChrome,
    Type,
  } from '../../ui/theme'
  import { safeTop } from '../../ui/safeArea'
  import { SNAP_MINI } from '../Rider/shell'
  import RouteBadge from '../RouteBadge.svelte'

  interface Point {
    x: number
    y: number
  }

  const watchedVehicleId = storeSelect(useVehicleWatchStore, (s) => s.watchedVehicleId)
  const trackedVehicleId = storeSelect(useRiderStore, (s) => s.trackedVehicleId)
  const watchingBoardedVehicle = $derived(
    watchedVehicleId.value != null && watchedVehicleId.value === trackedVehicleId.value,
  )
  const vehicles = storeSelect(useTransitStore, (s) => s.vehicles)
  const vehicle = $derived(
    watchedVehicleId.value ? vehicles.value[watchedVehicleId.value] : undefined,
  )
  const routeColor = $derived(getRouteColor(vehicle?.routeId ?? ''))

  // Watch mode follows buses too, so the bullet takes the same measured fit as every other
  // one in the app: 15px for a short id, less for anything that would reach the rim.
  const BULLET_BOX = 34

  // ...and for the same reason the camera cannot call itself a train camera: the riding
  // panel's onboard toggle offers it on every face, bus included, and that toggle is now the
  // only way in — the map's vehicle sheet used to carry a "Watch in 3D" button and no longer
  // does. Falls back to the generic noun rather than "train" while the vehicle is out of the
  // feed, which is the one case where its type is genuinely unknown — and is exactly what the
  // banner below is reporting.
  const watchedNoun = $derived(
    vehicle ? (vehicle.type === 'bus' ? 'bus' : 'train') : 'vehicle',
  )
  const bulletLabel = $derived(vehicle ? routeBadgeLabel(vehicle.routeId) : '?')
  const bulletPx = $derived(bulletFontPx(bulletLabel, BULLET_BOX, 15))

  const pointers = new Map<number, Point>()
  let pinchDistance: number | null = null
  let dragging = $state(false)

  function onPointerDown(event: PointerEvent) {
    const target = event.currentTarget as HTMLDivElement
    target.setPointerCapture(event.pointerId)
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
    dragging = true
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()]
      pinchDistance = Math.hypot(a.x - b.x, a.y - b.y)
    }
  }

  function onPointerMove(event: PointerEvent) {
    const previous = pointers.get(event.pointerId)
    if (!previous) return
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
    const watch = useVehicleWatchStore.getState()

    if (pointers.size === 1) {
      watch.orbitBy((event.clientX - previous.x) * 0.32, (previous.y - event.clientY) * 0.18)
      return
    }

    const [a, b] = [...pointers.values()]
    const distance = Math.hypot(a.x - b.x, a.y - b.y)
    if (pinchDistance != null) watch.zoomBy((distance - pinchDistance) * 0.012)
    pinchDistance = distance
  }

  function releasePointer(event: PointerEvent) {
    pointers.delete(event.pointerId)
    pinchDistance = null
    dragging = pointers.size > 0
  }

  function onWheel(event: WheelEvent) {
    event.preventDefault()
    useVehicleWatchStore.getState().zoomBy(-event.deltaY * 0.002)
  }

  function onKeyDown(event: KeyboardEvent) {
    const watch = useVehicleWatchStore.getState()
    if (event.key === 'ArrowLeft') watch.orbitBy(-8, 0)
    else if (event.key === 'ArrowRight') watch.orbitBy(8, 0)
    else if (event.key === 'ArrowUp') watch.orbitBy(0, 4)
    else if (event.key === 'ArrowDown') watch.orbitBy(0, -4)
    else if (event.key === '+' || event.key === '=') watch.zoomBy(0.35)
    else if (event.key === '-') watch.zoomBy(-0.35)
    else if (event.key === 'Escape') watch.stopWatching()
    else return
    event.preventDefault()
  }
</script>

{#if watchedVehicleId.value}
  <button
    type="button"
    aria-label="Third-person {watchedNoun} camera. Drag to orbit and pinch or scroll to zoom."
    tabindex="0"
    onpointerdown={onPointerDown}
    onpointermove={onPointerMove}
    onpointerup={releasePointer}
    onpointercancel={releasePointer}
    onwheel={onWheel}
    onkeydown={onKeyDown}
    style="
      position: absolute; inset: 0; z-index: 45;
      cursor: {dragging ? 'grabbing' : 'grab'};
      touch-action: none; outline: none; border: 0; padding: 0; background: transparent;
    "
  ></button>

  <!-- ⚠️ CURRENTLY UNREACHABLE, and kept deliberately. This banner is the affordance for
       watching a vehicle you are NOT aboard, and the riding panel's 3D toggle is now the
       only way into watch mode — so the watched vehicle is always the boarded one and this
       is always false. It stays because the condition is the correct rule, not because the
       markup is in use: restore any entry point that watches without boarding (the map's
       vehicle sheet had one) and the banner is what tells the rider what they are looking
       at and how to get out. Deleting it would silently ship that entry point with no exit. -->
  {#if !watchingBoardedVehicle}
    <div
      style="
        position: fixed; top: {safeTop(62)}; left: 12px; right: 12px; z-index: 210;
        display: flex; align-items: center; gap: 10px;
        padding: 9px 10px;
        background-color: {GLASS_BG}; backdrop-filter: blur(10px);
        {panelFrame()} pointer-events: auto;
      "
    >
      <RouteBadge routeId={vehicle?.routeId ?? ''} size={36} />
      <div style="flex: 1; min-width: 0;">
        <div style="color: {Colors.text}; {Type.bodyStrong};">
          {vehicle ? `Watching ${vehicle.label ?? vehicle.vehicleId}` : `Reconnecting to ${watchedNoun}...`}
        </div>
        <div style="color: {Colors.textSecondary}; {Type.caption}; margin-top: 2px;">
          Drag to orbit &middot; pinch or scroll to zoom
        </div>
      </div>
      <button
        onclick={() => useVehicleWatchStore.getState().stopWatching()}
        style="
          {tileButtonChrome}
          color: {Colors.text}; {Type.caption};
          padding: 7px 10px; border-radius: 8px; cursor: pointer;
        "
      >Exit</button>
    </div>
  {/if}

  <!-- Zoom / reset stack. ABSOLUTE, not fixed, and cleared of the rider drawer by its own mini
       height: entering watch mode parks that drawer at SNAP_MINI (see RidingPanel), and the
       drawer now rises from the bottom, so a viewport-anchored stack sat inside its mini face —
       on top of it, since this outranks it (210 against 150), landing three buttons over the
       3D toggle and ✕ that are the way OUT of this camera. Absolute puts it in MapPage's own
       box, whose bottom edge is the top of the tab bar, which is also the drawer's floor: one
       SNAP_MINI plus a gap clears it and no navigation inset is spent twice.
       ⚠️ Dragging the drawer taller while watching walks the sign back under these. Reachable,
       and left alone: watch mode is only entered from that drawer's toggle, which parks it at
       mini, and the fix is either a published drawer height or clamping the drag — both bigger
       than the overlap. -->
  <div
    style="
      position: absolute; right: 12px; bottom: {SNAP_MINI + 12}px; z-index: 210;
      display: flex; flex-direction: column; gap: 7px; pointer-events: auto;
    "
  >
    <button
      aria-label="Zoom in"
      onclick={() => useVehicleWatchStore.getState().zoomBy(0.35)}
      class="watch-button"
      style={tileButtonChrome}
    >+</button>
    <button
      aria-label="Reset chase camera"
      onclick={() => useVehicleWatchStore.getState().resetCamera()}
      class="watch-button reset"
      style={tileButtonChrome}
    >&#8982;</button>
    <button
      aria-label="Zoom out"
      onclick={() => useVehicleWatchStore.getState().zoomBy(-0.35)}
      class="watch-button"
      style={tileButtonChrome}
    >&minus;</button>
  </div>
{/if}

<style>
  .watch-button {
    width: 42px;
    height: 42px;
    padding: 0;
    border-radius: 9px;
    color: var(--bjl-color-text, #fff);
    font-size: 22px;
    font-weight: 700;
    cursor: pointer;
  }

  .watch-button.reset {
    font-size: 15px;
  }
</style>
